import { AlertMessage } from "../components/alerts";
import { useState, type FormEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowRight,
  Eye,
  EyeOff,
  LockKeyhole,
  UserRound,
  AlertCircle,
} from "lucide-react";
import type { SessionUser } from "@compila/shared";
import { api, ApiError } from "../lib/api.js";
import { Button } from "../components/motion/button/base";
import { Input } from "../components/motion/input";

export function LoginPage(): React.JSX.Element {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const queryClient = useQueryClient();

  const login = useMutation({
    mutationFn: () =>
      api.post<Pick<SessionUser, "role" | "name">>("/auth/login", {
        username,
        password,
      }),
    onSuccess: async () => {
      // Recarrega a sessao: o App decide para onde ir pelo papel.
      await queryClient.invalidateQueries({ queryKey: ["me"] });
    },
  });

  const onSubmit = (e: FormEvent): void => {
    e.preventDefault();
    if (!login.isPending) login.mutate();
  };

  const erro =
    login.error instanceof ApiError
      ? login.error.code === "RATE_LIMITED"
        ? "Muitas tentativas. Aguarde um minuto e tente de novo."
        : login.error.message
      : login.error
        ? "Não foi possível entrar. Tente de novo."
        : null;

  return (
    <div className="login-shell flex min-h-svh flex-col bg-background">
      <a
        href="#login-form"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:rounded-lg focus:bg-primary focus:p-3 focus:text-primary-foreground"
      >
        Pular para o login
      </a>
      <main className="flex min-h-svh flex-1 flex-col">
        <div className="flex flex-1 items-center justify-center px-6 py-12 sm:px-10 sm:py-16">
          <section className="w-full max-w-[400px]" aria-labelledby="login-title">
            <div className="mb-8">
              <h1
                id="login-title"
                className="text-3xl font-semibold tracking-tight sm:text-[34px] sm:leading-tight"
              >
                Maratona Nerds
              </h1>
              <p className="mt-4 text-sm leading-6 text-muted-foreground">
                Entre com suas credenciais para acessar a maratona de programação.
              </p>
            </div>

            <form
              id="login-form"
              onSubmit={onSubmit}
              className="space-y-5"
              aria-busy={login.isPending}
            >
              <Input
                id="username"
                name="username"
                label="Usuário"
                placeholder="Seu usuário"
                leftIcon={<UserRound aria-hidden />}
                value={username}
                onChange={setUsername}
                autoComplete="username"
                autoCapitalize="none"
                spellCheck={false}
                required
                disabled={login.isPending}
                classNames={{
                  field: "bg-card border-input",
                  label: "px-0",
                  input: "placeholder:text-muted-foreground",
                }}
              />
              <Input
                id="password"
                name="password"
                label="Senha"
                placeholder="Sua senha"
                type={showPassword ? "text" : "password"}
                leftIcon={<LockKeyhole aria-hidden />}
                rightIcon={
                  <Button
                    variant="ghost"
                    size="icon"
                    type="button"
                    aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                    aria-pressed={showPassword}
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={login.isPending}
                  >
                    {showPassword ? <EyeOff aria-hidden /> : <Eye aria-hidden />}
                  </Button>
                }
                value={password}
                onChange={setPassword}
                autoComplete="current-password"
                required
                disabled={login.isPending}
                classNames={{
                  field: "bg-card border-input",
                  label: "px-0",
                  input: "placeholder:text-muted-foreground",
                }}
              />

              {erro && (
                <AlertMessage tone="error">
                  <AlertCircle
                    size={18}
                    className="mt-0.5 shrink-0 text-destructive"
                    aria-hidden
                  />
                  <span>{erro}</span>
                </AlertMessage>
              )}

              <Button
                type="submit"
                aria-label={login.isPending ? "Entrando" : "Entrar na plataforma"}
                size="lg"
                className="w-full rounded-xl text-sm"
                pressScale={0.98}
                disabled={login.isPending}
              >
                <span role="status">
                  {login.isPending ? "Entrando…" : "Entrar na plataforma"}
                </span>
                {!login.isPending && <ArrowRight size={17} aria-hidden />}
              </Button>
            </form>

            <p className="mt-5 text-center text-xs leading-5 text-muted-foreground">
              Use o acesso fornecido pela organização.
              <br />
              Precisa de ajuda? Procure a equipe do evento.
            </p>
          </section>
        </div>
      </main>
    </div>
  );
}
